import {BookingModel} from "../booking.model";
import {IBookingInsuranceViewModel} from "./booking-insurance.view-model.interface";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IInsuranceProviderModel} from "./providers/insurance-provider-model.interface";
import {NeatInsuranceProviderModel} from "./providers/neat/neat-insurance-provider.model";
import {
    IBeginPaymentInsuranceSubscriptionRequest
} from "../../../airline-webapi/requests/begin-booking-payment.request";
import {IInsurancePaymentPageQuotesRequest} from "../../../airline-webapi/requests/booking-payment-methods.request";
import {IInsuranceQuotesResponse} from "../../../airline-webapi/responses/insurance-quotes.response";
import {BookingSessionStorageKeys} from "../storage/booking-session-storage-keys";
import {IInsuranceProductModel} from "./models/insurance-product.model.interface";
import {SellInsuranceMutation} from "../mutation-actions/insurance/sell-insurance.mutation";
import {RemoveInsuranceMutation} from "../mutation-actions/insurance/remove-insurance.mutation";
import {IServiceFactory} from "../../../service-factory.interface";
import {
    IInsuranceProviderConfigurationViewModel
} from "../../../configuration/objects/insurance-configuration.interfaces";
import {
    IProviderPaymentPageSellStrategyModel
} from "./sell-strategies/provider-payment-page-sell-strategy.model.interface";
import {InsuranceSellContextEnum} from "./insurance-sell-context.enum";
import {BookingQueuesEnum} from "../../../dot-rez-api/data-contracts/enums/booking-queues.enum";
import {BookingSystemCodesEnum} from "../../../dot-rez-api/data-contracts/enums/booking-system-codes.enum";


export class BookingInsuranceModel implements IBookingInsuranceViewModel {
    constructor(private readonly booking: BookingModel) {

        this._providers = this._createProviders();

        this.setPaymentPageQuotes(this.booking.storage.getJson(BookingSessionStorageKeys.insurancePaymentPageQuotes));

        makeObservable<this, '_insuranceInPaymentPageStrategy'>(this, {
            _insuranceInPaymentPageStrategy: observable.ref
        });

        this._reactions.push(reaction(() => this.booking.paymentHandler.holdBooking,
            async (holdBooking) => {
                if(holdBooking) {
                    await this.booking.mutationsManager.waitForMutations();
                    await this._insuranceInPaymentPageStrategy?.resetSelection();
                }
        }));
    }

    private _reactions: IReactionDisposer[] = [];

    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private readonly _providers: IInsuranceProviderModel[];

    private _insuranceInPaymentPageStrategy: IProviderPaymentPageSellStrategyModel | null = null;
    get insuranceInPaymentPageStrategy(): IProviderPaymentPageSellStrategyModel | null {
        if(this.booking.paymentHandler.holdBooking) {
            return null;
        }
        return this._insuranceInPaymentPageStrategy;
    }

    private set insuranceInPaymentPageStrategy(value: IProviderPaymentPageSellStrategyModel | null) {
        runInAction(() => {
            this._insuranceInPaymentPageStrategy = value;
        })
    }

    private _createProviders(): IInsuranceProviderModel[] {

        if(!this.booking.bookingStrategyAdapter.allowSellInsurance) {
            return [];
        }

        const providers: IInsuranceProviderModel[] = [];

        const providersConfigs = this.services.configuration.insurance.getAllProvidersConfigs();
        for(const providerConfig of providersConfigs) {
            if(providerConfig.isEnabled) {
                const p = this._createProviderFromConfig(providerConfig);
                if(p) {
                    providers.push(p);
                }
            }
        }

        return providers;
    }


    private _createProviderFromConfig(config: IInsuranceProviderConfigurationViewModel): IInsuranceProviderModel | null {
        return null;
/*
        switch (config.code.toUpperCase()) {
            case "NEAT":
                return new NeatInsuranceProviderModel(this.booking, config);
            default:
                return null;
        }
 */
    }


    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    get hasInsurance(): boolean {
        return this.booking.passengers.some(p => p.fees.some(fee => Boolean(fee.insuranceNote) ))
    }


    get isClaimed(): boolean {

        if(this.booking.isInQueue(BookingQueuesEnum.CancelForAnyReasonInsurance)) {
            return true;
        }

        return this.booking.owningSystemCodes.includes(BookingSystemCodesEnum.U1);

    }


    async buildPaymentPageQuotesRequest(): Promise<IInsurancePaymentPageQuotesRequest | null> {
        const provider = this._getProviderForPaymentPage();
        if(!provider) {
            return null;
        }

        await this.booking.mutationsManager.waitForMutations();
        const balanceDueInLocalStorage = this._getBalanceDueFromStorage(provider.providerCode, InsuranceSellContextEnum.PaymentPage);
        if(this.booking.balanceDue.amount === balanceDueInLocalStorage) {
            return null;
        }
        await this.insuranceInPaymentPageStrategy?.resetSelection();
        return this._getProviderForPaymentPage()?.buildPaymentPageQuotesRequest() ?? null;
    }



    setPaymentPageQuotes(quotes: IInsuranceQuotesResponse | null): void {
        if(!quotes) {
            this.booking.storage.removeItem(BookingSessionStorageKeys.insurancePaymentPageQuotes);
            this.insuranceInPaymentPageStrategy = null;
            return;
        }

        const provider = this._findProviderByCode(quotes?.providerCode);

        this.insuranceInPaymentPageStrategy = provider?.createPaymentPageSellStrategy(quotes) ?? null;

        this.booking.storage.setJson(BookingSessionStorageKeys.insurancePaymentPageQuotes, quotes);
    }

    buildSubscriptionRequests(): IBeginPaymentInsuranceSubscriptionRequest[] {
        const subscriptionRequests: IBeginPaymentInsuranceSubscriptionRequest[] = [];

        const forPaymentPage = this.insuranceInPaymentPageStrategy?.buildSubscriptionRequest();
        if(forPaymentPage) {
            subscriptionRequests.push(forPaymentPage);
        }

        return subscriptionRequests;
    }

    private _findProviderByCode(providerCode: string): IInsuranceProviderModel | null {
        providerCode = providerCode.toUpperCase();
        return this._providers.find(p => p.providerCode === providerCode) ?? null;
    }

    private _getProviderForPaymentPage(): IInsuranceProviderModel | null {
        const p = this._providers.find(p => p.supportsSellInPaymentPage);
        if(p) {
            return p;
        }

        return null;
    }


    async claim(): Promise<void> {

        await this.services.loadingIndicator.execute({
            action: async () => {
                await this.booking.discardChanges();
                await this.booking.services.airlineWebapi.claimInsurance({
                    dotRezToken: this.booking.session.token
                });

                const {bookingData} = await this.booking.session.bookingStateQueryBuilder().useBookingData().getBookingState();
                this.booking.updateBookingSessionData({
                    bookingData: bookingData,
                });
            }
        })


    }

    private _getBalanceDueStorageDiscriminator(providerCode: string, sellContext: InsuranceSellContextEnum): string {
        return `${providerCode}_${sellContext}`
    }

    private _getBalanceDueFromStorage(providerCode: string, sellContext: InsuranceSellContextEnum): number {
        const value = this.booking.storage.getItem(BookingSessionStorageKeys.insuranceBalanceDueAfterSelection, {
            discriminator: this._getBalanceDueStorageDiscriminator(providerCode, sellContext)
        });

        if(value) {
            return parseFloat(value);
        }

        return 0;
    }

    private async _setBalanceDueInStorage(providerCode: string, sellContext: InsuranceSellContextEnum): Promise<void> {
        await this.booking.mutationsManager.waitForMutations();
        this.booking.storage.setItem(BookingSessionStorageKeys.insuranceBalanceDueAfterSelection, this.booking.balanceDue.amount.toString(), {
            discriminator: this._getBalanceDueStorageDiscriminator(providerCode, sellContext)
        });
    }

    private _removeBalanceDueFromStorage(providerCode: string, sellContext: InsuranceSellContextEnum): void {
        this.booking.storage.removeItem(BookingSessionStorageKeys.insuranceBalanceDueAfterSelection, {
            discriminator: this._getBalanceDueStorageDiscriminator(providerCode, sellContext)
        });
    }

    removeProduct(product: IInsuranceProductModel, sellContext: InsuranceSellContextEnum): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.booking.mutationsManager.startMutation(new RemoveInsuranceMutation(product, this.booking, {
                onCompleted: async () => {
                    this._removeBalanceDueFromStorage(product.providerCode, sellContext);
                    resolve();
                },
                onError: (err) => {
                    reject(err);
                }
            }))
        })
    }

    sellProduct(product: IInsuranceProductModel, sellContext: InsuranceSellContextEnum): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            this.booking.mutationsManager.startMutation(new SellInsuranceMutation(product, this.booking, {
                onCompleted: async () => {
                    await this._setBalanceDueInStorage(product.providerCode, sellContext);
                    resolve();
                },
                onError: (err) => {
                    reject(err);
                }
            }))
        })
    }
}


